import { useMutation, useQuery } from '@apollo/client';
import { useSession } from '@/components/session/useSession';
import { RESET_SANDBOX_FEATURE_FLAGS, UPDATE_SANDBOX_FEATURE_FLAG } from '../graphql/SandboxFeatureFlags.mutations';
import { GET_DEFAULT_FEATURE_FLAG_VALUES } from '../graphql/SandboxFeatureFlags.queries';
import { GET_CURRENT_FEATURE_FLAGS, GET_SESSION_DATA } from '../graphql/Users.queries';

export function useSandboxFeatureFlags({ isSandboxOrDev }: { isSandboxOrDev: boolean }) {
  const { updateFeatureFlags } = useSession();

  const { data: defaultData, loading: defaultLoading } = useQuery(GET_DEFAULT_FEATURE_FLAG_VALUES, {
    skip: !isSandboxOrDev,
  });
  const { data: currentData, loading: currentLoading } = useQuery(GET_CURRENT_FEATURE_FLAGS, {
    skip: !isSandboxOrDev,
  });

  const [updateFlagMutation, { loading: updateLoading }] = useMutation(UPDATE_SANDBOX_FEATURE_FLAG, {
    refetchQueries: [GET_SESSION_DATA, GET_CURRENT_FEATURE_FLAGS],
    awaitRefetchQueries: true,
  });

  const [resetFlagsMutation, { loading: resetLoading }] = useMutation(RESET_SANDBOX_FEATURE_FLAGS, {
    refetchQueries: [GET_SESSION_DATA, GET_CURRENT_FEATURE_FLAGS],
    awaitRefetchQueries: true,
  });

  const updateFeatureFlag = async (variables: { flag: string; value: boolean }) => {
    try {
      await updateFlagMutation({
        variables,
      });
      // The refetch will automatically update the session data
    } catch (error) {
      console.error('Failed to update feature flag:', error);
      throw error;
    }
  };

  const resetFeatureFlags = async () => {
    try {
      await resetFlagsMutation();
      // Clear local playground flags after successful reset
      updateFeatureFlags?.({});
    } catch (error) {
      console.error('Failed to reset feature flags:', error);
      throw error;
    }
  };

  return {
    featureFlagDefaultValues: defaultData?.featureFlagDefaultValues,
    currentFeatureFlags: currentData?.getUserFeatureFlags,
    loading: defaultLoading || currentLoading || updateLoading || resetLoading,
    updateFeatureFlag,
    resetFeatureFlags,
  };
}
